import React from 'react';
import { Badge } from 'reactstrap';

import { AuthTags } from '~constants/authTags';
import { isBudgetOpen, isCommonwealthUnspent } from '~support-at-home/constants/sahBudgets';

import { ClickableIcon } from '~components';
import { faMoneyBill, faRepeat } from '~components/icon';
import { Row } from '~components/table-view';

interface ComponentProps {
  row: Row;
  saConnected: boolean;
  isDirty: boolean;
  isProcessing: boolean;
  canElectToReturn: boolean;
  onRollover: (row: Row) => void;
  onElectToReturn: (row: Row) => void;
}

// TAV-2601 / TAV-2602 — the "sah_actions" custom cell for the per-client SAH budgets table.
// Renders a read-only status badge (never a column id — see Decision E2: a `status` column would
// let a stale local 'OPEN' be PUT back after a successful rollover and revert the closure) plus
// the two irreversible-action triggers, gated per Decision E3 / F.
const SahBudgetActions: React.FC<ComponentProps> = ({
  row,
  saConnected,
  isDirty,
  isProcessing,
  canElectToReturn,
  onRollover,
  onElectToReturn,
}) => {
  const status = row.status as string | null | undefined;
  const open = isBudgetOpen(status);

  // An unsaved row has no server record and no sah_budget_id to send. Blocked by connectivity,
  // status or budget type means "not offered at all" (render nothing); blocked by isDirty /
  // isProcessing means "not right now" (render disabled with a tooltip, below).
  const actionable = row.persisted === true && !!row.backendId && saConnected && open;
  let blockedReason: string | null = null;
  if (isDirty) {
    blockedReason = 'Save or discard your changes before continuing';
  } else if (isProcessing) {
    blockedReason = 'Please wait for the current save to finish';
  }

  // Already-actioned gates. The API is the authority here - loadBudgetForAction rejects a repeat
  // outright, and the trigger claims the row before it calls the vendor - so these are about not
  // OFFERING a button whose only outcome is a 400. Gate on the action columns rather than `status`
  // for the same reason the API does: status carries allowUpdate:true and is rewritten verbatim
  // from the live API on every refresh, so it re-opens on an already-actioned budget the moment SA
  // still reports OPEN. rollover_requested_at / is_elected_to_return carry allowUpdate:false and
  // survive a refresh. Both actions are gated, in lock-step with the API guard.
  const canOfferRollover = !row.rollover_requested_at;
  const canOfferElectToReturn =
    canElectToReturn &&
    isCommonwealthUnspent(row.budget_type_code as string | null | undefined) &&
    !row.is_elected_to_return;

  return (
    // flex-nowrap keeps the badges and icons on one line, so the column grows to fit them instead
    // of stacking CLOSED above "Funds returned". px-1 matches the 0.25rem side padding compact
    // mode gives read-only cells, which a custom cell in an edit row does not get. The column's
    // cell-align-center is only text-align, so justify-content-center centres this flex row.
    <div className="d-flex flex-nowrap align-items-center justify-content-center gap-2 px-1">
      {status && (
        <Badge color={open ? 'success' : 'secondary'} data-cy={`sah-budget-status-badge-${row.id}`}>
          {status}
        </Badge>
      )}
      {row.is_elected_to_return && (
        <Badge color="info" data-cy={`sah-budget-elected-badge-${row.id}`}>
          Funds returned
        </Badge>
      )}
      {actionable && (
        <>
          {canOfferRollover && (
            <ClickableIcon
              id={`sah-budget-rollover-${row.id}`}
              icon={faRepeat}
              onClick={() => onRollover(row)}
              disabled={isDirty || isProcessing}
              showTooltipWhenDisabled
              toolTip={blockedReason ?? 'Roll over budget'}
              ariaLabel="Roll over budget"
              authTag={AuthTags.CLIENTS.BUDGETS.UPDATE}
              dataCy={`sah-budget-rollover-icon-${row.id}`}
            />
          )}
          {canOfferElectToReturn && (
            <ClickableIcon
              id={`sah-budget-elect-to-return-${row.id}`}
              icon={faMoneyBill}
              onClick={() => onElectToReturn(row)}
              disabled={isDirty || isProcessing}
              showTooltipWhenDisabled
              toolTip={blockedReason ?? 'Elect to return unspent funds'}
              ariaLabel="Elect to return unspent funds"
              authTag={AuthTags.CLIENTS.BUDGETS.ELECTTORETURN}
              dataCy={`sah-budget-elect-to-return-icon-${row.id}`}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SahBudgetActions;
